import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AudienceExperienceStatusEnum } from 'app/library/audience/enums/audience-experience-status.enum';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { AudienceApiService } from 'app/library/audience/services/audience.api-service';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';

@Component({
    selector: 'app-relevant-experiences',
    templateUrl: './relevant-experiences.component.html',
    styleUrls: ['./relevant-experiences.component.scss'],
})
export class RelevantExperiencesComponent extends UnsubscribeOnDestroy implements OnInit {
    @HostListener('window:resize', ['$event'])
    onResize(_event?: UIEvent) {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        if (width > 1080) {
            this.isLargeScreen = true;
        } else {
            this.isLargeScreen = false;
        }
    }

    audience: MaxBrainAudience;
    widgets: AudienceRelevantExperience[] = [];
    audienceId: string;
    isLargeScreen = false;
    AudienceExperienceStatusEnum = AudienceExperienceStatusEnum;
    private _parentExperienceStatusById = new Map<string, AudienceExperienceStatusEnum>();

    constructor(
        public sidenavService: SidenavService,
        private _route: ActivatedRoute,
        private _audienceService: AudienceService,
        private _audienceApiService: AudienceApiService,
        private _translateService: TranslateService,
        private _location: Location
    ) {
        super();

        this.onResize();
    }

    private async loadAudience(audienceId: string): Promise<MaxBrainAudience | null> {
        let audience = this._audienceService.getAudienceById(audienceId);
        if (audience) {
            return audience;
        }

        // Fallback to fetching the single audience by id.
        // `fetchAudiences()` loads only a list (often root audiences) and may not contain this id.
        try {
            audience = await this._audienceApiService.read(audienceId).toPromise();
            return audience ?? null;
        } catch {
            return null;
        }
    }

    async ngOnInit() {
        this.audienceId = this._route.parent.snapshot.params['id'];
        this.audience = await this.loadAudience(this.audienceId);

        if (this.audience?.parent?.id) {
            try {
                const parentExperiences = await this._audienceApiService.fetchAudienceRelevantExperiences(this.audience.parent.id).toPromise();
                parentExperiences?.forEach((x) => this._parentExperienceStatusById.set(x.experience.id, x.status));
            } catch {
                // If parent experiences can't be loaded, don't block the UI.
                this._parentExperienceStatusById.clear();
            }
        }

        const audienceRelevantExperiences = await this._audienceService.fetchAudienceRelevantExperiences(this.audienceId);

        for (let i = 0; i < audienceRelevantExperiences.length; i++) {
            const widget = audienceRelevantExperiences[i];
            this.widgets.push(widget);
        }
    }

    private isDisabledBecauseParentOff(): boolean {
        // Only relevant for child audiences
        return !!this.audience?.parent && this.audience.parent.hasRelevantExperiences === false;
    }

    private isDisabledBecauseNotRelevantInParent(widget: AudienceRelevantExperience): boolean {
        // Only relevant for child audiences
        if (!this.audience?.parent) {
            return false;
        }

        const parentStatus = this._parentExperienceStatusById.get(widget.experience.id);
        return parentStatus !== AudienceExperienceStatusEnum.RELEVANT;
    }

    isToggleDisabled(widget: AudienceRelevantExperience): boolean {
        if (!this.audience?.parent) {
            return false;
        }

        if (this.isDisabledBecauseParentOff()) {
            return true;
        }

        return this.isDisabledBecauseNotRelevantInParent(widget);
    }

    getToggleDisabledTooltip(widget: AudienceRelevantExperience): string {
        if (!this.isToggleDisabled(widget)) {
            return '';
        }

        if (this.isDisabledBecauseParentOff()) {
            return this._translateService.instant('ENGAGE.AUDIENCE.TOOLTIPS.RELEVANT_EXPERIENCE_DISABLED_PARENT_OFF');
        }

        return this._translateService.instant('ENGAGE.AUDIENCE.TOOLTIPS.RELEVANT_EXPERIENCE_DISABLED_NOT_RELEVANT_IN_PARENT');
    }

    async updateStatus(widget: AudienceRelevantExperience, status: boolean): Promise<void> {
        const newStatus = status ? AudienceExperienceStatusEnum.RELEVANT : AudienceExperienceStatusEnum.NOT_RELEVANT;

        const isUpdated = await this._audienceService.updateAudienceExperienceStatus(this.audienceId, widget.experience.id, newStatus);

        if (isUpdated) {
            widget.status = newStatus;
        }
    }

    goBack() {
        this._location.back();
    }
}
